// Phase 2F: secure Chrome extension connect flow — pure, side-effect-free
// helpers only (no React, no session store, no api client, no browser
// globals) so they can be unit tested under this repo's default plain-Node
// vitest environment, with no per-file jsdom docblock.

export type ConnectParams = {
  clientId: string;
  redirectUri: string;
  codeChallenge: string;
  codeChallengeMethod: string;
  state: string | null;
};

export function parseConnectParams(search: string): ConnectParams {
  const params = new URLSearchParams(search);
  return {
    clientId: params.get('client_id') || '',
    redirectUri: params.get('redirect_uri') || '',
    codeChallenge: params.get('code_challenge') || '',
    codeChallengeMethod: params.get('code_challenge_method') || '',
    state: params.get('state'),
  };
}

export type ConnectValidation =
  | { ok: true }
  | { ok: false; reason: 'not_configured' | 'mismatch' | 'invalid_pkce' };

// Exact-match gate only. The incoming client_id/redirect_uri must be
// byte-for-byte equal to the operator-configured expected values — never a
// generic chromiumapp.org pattern check, never a prefix/partial match. A
// missing expected value (feature not configured for this deployment) fails
// closed exactly like a mismatch. This is a defense-in-depth/UX gate; the
// backend's own exact-match check in `_require_configured_client` remains
// fully authoritative and is not weakened or replaced by this.
export function validateConnectRequest(
  params: ConnectParams,
  expected: { clientId: string; redirectUri: string }
): ConnectValidation {
  if (!expected.clientId || !expected.redirectUri) {
    return { ok: false, reason: 'not_configured' };
  }
  if (params.clientId !== expected.clientId || params.redirectUri !== expected.redirectUri) {
    return { ok: false, reason: 'mismatch' };
  }
  if (params.codeChallengeMethod !== 'S256' || params.codeChallenge.length < 43 || params.codeChallenge.length > 128) {
    return { ok: false, reason: 'invalid_pkce' };
  }
  return { ok: true };
}

// Approve path: navigates only after a successful, backend-validated
// /extension/auth/authorize call, using the backend-echoed redirect_uri.
export function buildApproveRedirectUrl(redirectUri: string, code: string, state: string | null | undefined): string {
  const url = new URL(redirectUri);
  url.searchParams.set('code', code);
  if (state) url.searchParams.set('state', state);
  return url.toString();
}

// Cancel path: navigates using only the already exact-matched expected
// redirect_uri (never the raw query string, never a regex-matched
// fallback), with no backend call at all.
export function buildCancelRedirectUrl(redirectUri: string, state: string | null): string {
  const url = new URL(redirectUri);
  url.searchParams.set('error', 'access_denied');
  if (state) url.searchParams.set('state', state);
  return url.toString();
}
